import React, { useState, useRef } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { motion, AnimatePresence } from 'framer-motion'
import { formatFileSize } from '../../services/storageService'

const { FiUpload, FiX, FiFile, FiAlertCircle, FiCheck } = FiIcons

const FileUpload = ({ 
  onFileSelect, 
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB default
  multiple = false,
  label = 'Upload File',
  description = 'Click to browse or drag and drop'
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const validateFile = (file) => {
    if (file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)}`
    }
    return null
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    setError('')

    const files = Array.from(e.dataTransfer.files)
    processFiles(files)
  }

  const handleChange = (e) => {
    e.preventDefault()
    setError('')

    const files = Array.from(e.target.files)
    processFiles(files)
  }

  const processFiles = (files) => {
    const validFiles = []
    let errorMsg = ''

    for (const file of files) {
      const validationError = validateFile(file)
      if (validationError) {
        errorMsg = validationError
        break
      }
      validFiles.push(file)
    }

    if (errorMsg) {
      setError(errorMsg)
      return
    }

    if (multiple) {
      setSelectedFiles([...selectedFiles, ...validFiles])
      onFileSelect([...selectedFiles, ...validFiles])
    } else {
      setSelectedFiles(validFiles)
      onFileSelect(validFiles[0])
    }
  }

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(newFiles)
    if (multiple) {
      onFileSelect(newFiles)
    } else {
      onFileSelect(null)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          className="hidden"
        />

        <div className="space-y-2">
          <SafeIcon
            icon={error ? FiAlertCircle : FiUpload}
            className={`text-4xl mx-auto ${
              error ? 'text-red-500' : dragActive ? 'text-blue-500' : 'text-gray-400'
            }`}
          />
          
          <div>
            <button
              type="button"
              onClick={handleButtonClick}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Click to upload
            </button>
            <span className="text-gray-600"> or drag and drop</span>
          </div>

          <p className="text-xs text-gray-500">
            {description} • Max {formatFileSize(maxSize)}
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600"
          >
            {error}
          </motion.div>
        )}
      </div>

      {/* Selected Files */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2"
          >
            {selectedFiles.map((file, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <SafeIcon icon={FiFile} className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <SafeIcon icon={FiCheck} className="text-green-600 flex-shrink-0" />
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FileUpload